import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory2Outlined as ContainerIcon,
  MapOutlined as MapIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { ContainerStatus } from '../../types/container.type';
import { LocationMapPicker } from './LocationMapPicker';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
} as const;

const formGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 2,
  '& > .full-width': { gridColumn: '1 / -1' },
} as const;

const capacityRowSx = {
  display: 'grid',
  gridTemplateColumns: '1fr 180px',
  gap: 2,
} as const;

function TabPanel({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

function parseOptionalNumber(value: string): number | undefined {
  return value === '' ? undefined : Number(value);
}

const FORM_TABS = [
  'containers.form.basicInfo',
  'containers.form.locationInstallation',
  'containers.form.contactInfo',
  'containers.form.collectionSettings',
  'containers.form.sensorSettings',
  'containers.form.settings',
  'containers.form.additionalInfo',
] as const;

export type ContainerFormState = {
  device_id: string;
  material_type_id: string;
  fill_level?: number;
  status: ContainerStatus;
  longitude?: number;
  latitude?: number;
  buffer?: number;
  collection_site_id: string;
  data: {
    name: string;
    description: string;
    capacity?: number;
    capacity_unit: string;
    location_name: string;
    installation_date: string;
    manufacturer: string;
    model: string;
    serial_number: string;
    warranty_expiry: string;
    notes: string;
    contact_person: string;
    contact_phone: string;
    contact_email: string;
    emergency_contact: string;
    access_instructions: string;
    special_handling: string;
    environmental_notes: string;
    maintenance_notes: string;
    sensor_calibration_date: string;
    sensor_accuracy?: number;
    fill_level_threshold?: number;
    temperature_threshold?: number;
    collection_frequency: string;
    collection_route: string;
    assigned_truck: string;
    assigned_driver: string;
    priority_level: string;
    is_accessible: boolean;
    requires_special_equipment: boolean;
    hazardous_materials: boolean;
    recycling_instructions: string;
    waste_segregation_required: boolean;
    contamination_threshold?: number;
    cost_per_collection?: number;
    environmental_impact_score?: number;
    materials: string[];
    sustainability_metrics: {
      co2_reduction: number;
      energy_saved: number;
      water_saved: number;
      trees_equivalent: number;
    };
    performance_metrics: {
      collection_efficiency: number;
      fill_rate_accuracy: number;
      maintenance_compliance: number;
      customer_satisfaction: number;
    };
  };
};

type LabeledOption = { value: string; labelKey: string };

type StatusOption = {
  value: ContainerStatus;
  labelKey: string;
  color: string;
};

type PriorityOption = {
  value: string;
  labelKey: string;
  color: string;
};

export interface ContainerFormDialogProps {
  open: boolean;
  onClose: () => void;
  isEdit: boolean;
  formData: ContainerFormState;
  setFormData: React.Dispatch<React.SetStateAction<ContainerFormState>>;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  materialTypes: Array<{ id: string; name: string }>;
  collectionSites: Array<{ id: string; data?: { name?: string } }>;
  unitOptions: LabeledOption[];
  statusOptions: StatusOption[];
  priorityOptions: PriorityOption[];
  frequencyOptions: LabeledOption[];
  materialTypesList: string[];
  getMaterialLabel: (value: string) => string;
  onSubmit: () => void;
  /** Charity orgs: financial fields stay at 0 and are not editable. */
  zeroFinancialFields?: boolean;
  /** Self-setup: spotlight the tabbed form sections. */
  tourFormHint?: boolean;
  /** Self-setup: spotlight the create/update submit button. */
  tourSubmitHint?: boolean;
  /** Help "Go to" flow: one tip on the whole form (no tab walk). */
  tourFormSingleStep?: boolean;
  onTourFormNext?: () => void;
  onTourSubmitDone?: () => void;
  onTourDismiss?: () => void;
}

export function ContainerFormDialog({
  open,
  onClose,
  isEdit,
  formData,
  setFormData,
  errors,
  setErrors,
  materialTypes,
  collectionSites,
  unitOptions,
  statusOptions,
  priorityOptions,
  frequencyOptions,
  materialTypesList,
  getMaterialLabel,
  onSubmit,
  zeroFinancialFields = false,
  tourFormHint = false,
  tourSubmitHint = false,
  tourFormSingleStep = false,
  onTourFormNext,
  onTourSubmitDone,
  onTourDismiss,
}: ContainerFormDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setMapPickerOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (errors.latitude || errors.longitude) {
      setActiveTab(1);
    }
    if (errors.collection_site_id) {
      setActiveTab(0);
    }
  }, [errors.latitude, errors.longitude, errors.collection_site_id]);

  const handleLocationPick = (latitude: number, longitude: number) => {
    setFormData({
      ...formData,
      latitude: Number(latitude.toFixed(6)),
      longitude: Number(longitude.toFixed(6)),
    });
    setErrors({ ...errors, latitude: '', longitude: '' });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            <ContainerIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {isEdit ? t('common.edit') : t('common.add')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}>
              {isEdit ? t('containers.editDialogTitle') : t('containers.addDialogTitle')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <DialogContent
          dividers
          sx={{
            px: 0,
            py: 0,
            overflow: 'auto',
            flex: '1 1 auto',
          }}
        >
          <SetupCoachmark
            open={tourFormHint && open && !isEdit}
            fullWidth
            title={t('selfSetup.tour.containersFormTitle')}
            subtitle={t('selfSetup.tour.containersFormSubtitle')}
            nextLabel={t('selfSetup.tourNext')}
            onNext={() => {
              if (!tourFormSingleStep && activeTab < FORM_TABS.length - 1) {
                setActiveTab(activeTab + 1);
                return;
              }
              onTourFormNext?.();
            }}
            onDismiss={() => onTourDismiss?.()}
          >
            <Box>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  px: 2.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  minHeight: 48,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    minHeight: 48,
                  },
                }}
              >
                {FORM_TABS.map((labelKey) => (
                  <Tab key={labelKey} label={t(labelKey)} />
                ))}
              </Tabs>

              <Box sx={{ px: 2.5, pb: 2 }}>
                <TabPanel value={activeTab} index={0}>
              <Box sx={formGridSx}>
                <TextField
                  label={t('containers.form.containerName')}
                  value={formData.data.name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      data: { ...formData.data, name: e.target.value },
                    });
                    setErrors({ ...errors, name: '' });
                  }}
                  required
                  error={!!errors.name}
                  helperText={errors.name}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.deviceId')}
                  value={formData.device_id}
                  onChange={(e) => {
                    setFormData({ ...formData, device_id: e.target.value });
                    setErrors({ ...errors, device_id: '' });
                  }}
                  required
                  error={!!errors.device_id}
                  helperText={errors.device_id}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <FormControl
                  fullWidth
                  required
                  size="small"
                  error={!!errors.material_type_id}
                  sx={fieldSx}
                >
                  <InputLabel>{t('containers.form.materialType')}</InputLabel>
                  <Select
                    value={formData.material_type_id}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        material_type_id: e.target.value,
                      });
                      setErrors({ ...errors, material_type_id: '' });
                    }}
                    label={t('containers.form.materialType')}
                  >
                    {materialTypes.map((material) => (
                      <MenuItem key={material.id} value={material.id}>
                        {material.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.material_type_id && (
                    <FormHelperText>{errors.material_type_id}</FormHelperText>
                  )}
                </FormControl>
                <Box className="full-width" sx={capacityRowSx}>
                  <TextField
                    label={t('common.capacity')}
                    type="number"
                    value={formData.data.capacity ?? ''}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        data: {
                          ...formData.data,
                          capacity: parseOptionalNumber(e.target.value),
                        },
                      });
                      setErrors({ ...errors, capacity: '' });
                    }}
                    required
                    error={!!errors.capacity}
                    helperText={errors.capacity}
                    inputProps={{ min: 0 }}
                    fullWidth
                    size="small"
                    sx={fieldSx}
                  />
                  <FormControl
                    fullWidth
                    required
                    size="small"
                    error={!!errors.capacity_unit}
                    sx={fieldSx}
                  >
                    <InputLabel>{t('common.unit')}</InputLabel>
                    <Select
                      value={formData.data.capacity_unit}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          data: {
                            ...formData.data,
                            capacity_unit: e.target.value,
                          },
                        });
                        setErrors({ ...errors, capacity_unit: '' });
                      }}
                      label={t('common.unit')}
                    >
                      {unitOptions.map((unit) => (
                        <MenuItem key={unit.value} value={unit.value}>
                          {t(unit.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.capacity_unit && (
                      <FormHelperText>{errors.capacity_unit}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t('common.status')}</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.value as ContainerStatus,
                      })
                    }
                    label={t('common.status')}
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: status.color,
                            }}
                          />
                          {t(status.labelKey)}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t('containers.form.priorityLevel')}</InputLabel>
                  <Select
                    value={formData.data.priority_level}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        data: {
                          ...formData.data,
                          priority_level: e.target.value,
                        },
                      })
                    }
                    label={t('containers.form.priorityLevel')}
                  >
                    {priorityOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: option.color,
                            }}
                          />
                          {t(option.labelKey)}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  fullWidth
                  required
                  size="small"
                  error={!!errors.collection_site_id}
                  sx={fieldSx}
                >
                  <InputLabel>{t('containers.form.collectionSite')}</InputLabel>
                  <Select
                    value={formData.collection_site_id}
                    label={t('containers.form.collectionSite')}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        collection_site_id: e.target.value,
                      });
                      setErrors({ ...errors, collection_site_id: '' });
                    }}
                  >
                    {collectionSites.map((site) => (
                      <MenuItem key={site.id} value={site.id}>
                        {site.data?.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.collection_site_id && (
                    <FormHelperText>{errors.collection_site_id}</FormHelperText>
                  )}
                </FormControl>
                <TextField
                  label={t('containers.form.bufferRate')}
                  type="number"
                  value={formData.buffer ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      buffer:
                        e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                  inputProps={{ min: 0, max: 100 }}
                  helperText={t('containers.form.bufferHelper')}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.currentFillLevel')}
                  type="number"
                  value={formData.fill_level ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fill_level:
                        e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                  inputProps={{ min: 0 }}
                  helperText={t('containers.form.currentFillHelper')}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Box sx={formGridSx}>
                <TextField
                  label={t('common.latitude')}
                  type="number"
                  value={formData.latitude ?? ''}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      latitude:
                        e.target.value === '' ? undefined : Number(e.target.value),
                    });
                    setErrors({ ...errors, latitude: '' });
                  }}
                  required
                  error={!!errors.latitude}
                  helperText={errors.latitude}
                  inputProps={{ step: 'any' }}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('common.longitude')}
                  type="number"
                  value={formData.longitude ?? ''}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      longitude:
                        e.target.value === '' ? undefined : Number(e.target.value),
                    });
                    setErrors({ ...errors, longitude: '' });
                  }}
                  required
                  error={!!errors.longitude}
                  helperText={errors.longitude}
                  inputProps={{ step: 'any' }}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <Box className="full-width">
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('containers.form.mapPickerHint')}
                  </Typography>
                  {open && activeTab === 1 && (
                    <LocationMapPicker
                      latitude={formData.latitude}
                      longitude={formData.longitude}
                      onLocationChange={handleLocationPick}
                      height={240}
                    />
                  )}
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<MapIcon />}
                    onClick={() => setMapPickerOpen(true)}
                    sx={{ mt: 1.5, textTransform: 'none' }}
                  >
                    {t('containers.form.pickOnMap')}
                  </Button>
                </Box>
                <TextField
                  label={t('containers.form.locationName')}
                  value={formData.data.location_name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      data: { ...formData.data, location_name: e.target.value },
                    });
                    setErrors({ ...errors, location_name: '' });
                  }}
                  required
                  error={!!errors.location_name}
                  helperText={errors.location_name}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.installationDate')}
                  type="date"
                  value={formData.data.installation_date}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        installation_date: e.target.value,
                      },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('common.manufacturer')}
                  value={formData.data.manufacturer}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, manufacturer: e.target.value },
                    })
                  }
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('common.model')}
                  value={formData.data.model}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, model: e.target.value },
                    })
                  }
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('common.serialNumber')}
                  value={formData.data.serial_number}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, serial_number: e.target.value },
                    })
                  }
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('common.warrantyExpiry')}
                  type="date"
                  value={formData.data.warranty_expiry}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        warranty_expiry: e.target.value,
                      },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Box sx={formGridSx}>
                <TextField
                  label={t('containers.form.contactPerson')}
                  value={formData.data.contact_person}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, contact_person: e.target.value },
                    })
                  }
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.contactPhone')}
                  value={formData.data.contact_phone}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, contact_phone: e.target.value },
                    })
                  }
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.contactEmail')}
                  type="email"
                  value={formData.data.contact_email}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, contact_email: e.target.value },
                    })
                  }
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.emergencyContact')}
                  value={formData.data.emergency_contact}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, emergency_contact: e.target.value },
                    })
                  }
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
              <Box sx={formGridSx}>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t('containers.form.collectionFrequency')}</InputLabel>
                  <Select
                    value={formData.data.collection_frequency}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        data: {
                          ...formData.data,
                          collection_frequency: e.target.value,
                        },
                      })
                    }
                    label={t('containers.form.collectionFrequency')}
                  >
                    {frequencyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t('containers.form.collectionRoute')}
                  value={formData.data.collection_route}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        collection_route: e.target.value,
                      },
                    })
                  }
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.assignedTruck')}
                  value={formData.data.assigned_truck}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        assigned_truck: e.target.value,
                      },
                    })
                  }
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.assignedDriver')}
                  value={formData.data.assigned_driver}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        assigned_driver: e.target.value,
                      },
                    })
                  }
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={4}>
              <Box sx={formGridSx}>
                <TextField
                  label={t('containers.form.sensorCalibrationDate')}
                  type="date"
                  value={formData.data.sensor_calibration_date}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        sensor_calibration_date: e.target.value,
                      },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.sensorAccuracy')}
                  type="number"
                  value={formData.data.sensor_accuracy ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        sensor_accuracy: parseOptionalNumber(e.target.value),
                      },
                    })
                  }
                  inputProps={{ min: 0, max: 100 }}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.fillLevelThreshold')}
                  type="number"
                  value={formData.data.fill_level_threshold ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        fill_level_threshold: parseOptionalNumber(e.target.value),
                      },
                    })
                  }
                  inputProps={{ min: 0, max: 100 }}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.temperatureThreshold')}
                  type="number"
                  value={formData.data.temperature_threshold ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        temperature_threshold: parseOptionalNumber(e.target.value),
                      },
                    })
                  }
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={5}>
              <Box sx={formGridSx}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.data.is_accessible}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          data: {
                            ...formData.data,
                            is_accessible: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label={t('containers.form.isAccessible')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.data.requires_special_equipment}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          data: {
                            ...formData.data,
                            requires_special_equipment: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label={t('containers.form.requiresSpecialEquipment')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.data.hazardous_materials}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          data: {
                            ...formData.data,
                            hazardous_materials: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label={t('containers.form.hazardousMaterials')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.data.waste_segregation_required}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          data: {
                            ...formData.data,
                            waste_segregation_required: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label={t('containers.form.wasteSegregationRequired')}
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={6}>
              <Box sx={formGridSx}>
                <TextField
                  label={t('common.description')}
                  value={formData.data.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, description: e.target.value },
                    })
                  }
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('common.notes')}
                  value={formData.data.notes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, notes: e.target.value },
                    })
                  }
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.accessInstructions')}
                  value={formData.data.access_instructions}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        access_instructions: e.target.value,
                      },
                    })
                  }
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.specialHandling')}
                  value={formData.data.special_handling}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        special_handling: e.target.value,
                      },
                    })
                  }
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.environmentalNotes')}
                  value={formData.data.environmental_notes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        environmental_notes: e.target.value,
                      },
                    })
                  }
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.maintenanceNotes')}
                  value={formData.data.maintenance_notes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        maintenance_notes: e.target.value,
                      },
                    })
                  }
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.recyclingInstructions')}
                  value={formData.data.recycling_instructions}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        recycling_instructions: e.target.value,
                      },
                    })
                  }
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.contaminationThreshold')}
                  type="number"
                  value={formData.data.contamination_threshold ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        contamination_threshold: parseOptionalNumber(e.target.value),
                      },
                    })
                  }
                  inputProps={{ min: 0, max: 100 }}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <Autocomplete
                  className="full-width"
                  multiple
                  options={materialTypesList}
                  getOptionLabel={(option) => getMaterialLabel(option)}
                  value={formData.data.materials}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      data: { ...formData.data, materials: newValue },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('containers.form.additionalMaterials')}
                      fullWidth
                      size="small"
                      placeholder={t('containers.form.additionalMaterialsPlaceholder')}
                      helperText={t('containers.form.additionalMaterialsHelper')}
                      sx={fieldSx}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={getMaterialLabel(option)}
                        {...getTagProps({ index })}
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        }}
                      />
                    ))
                  }
                />
                <TextField
                  label={t('containers.form.costPerCollection')}
                  type="number"
                  value={
                    zeroFinancialFields
                      ? 0
                      : (formData.data.cost_per_collection ?? '')
                  }
                  onChange={(e) => {
                    if (zeroFinancialFields) return;
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        cost_per_collection: parseOptionalNumber(e.target.value),
                      },
                    });
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  disabled={zeroFinancialFields}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label={t('containers.form.environmentalImpactScore')}
                  type="number"
                  value={formData.data.environmental_impact_score ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        environmental_impact_score: parseOptionalNumber(e.target.value),
                      },
                    })
                  }
                  inputProps={{ min: 0, max: 100 }}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
              </Box>
            </TabPanel>
              </Box>
            </Box>
          </SetupCoachmark>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            {t('common.cancel')}
          </Button>
          <SetupCoachmark
            open={tourSubmitHint && open && !isEdit}
            title={t('selfSetup.tour.containersSubmitTitle')}
            subtitle={t('selfSetup.tour.containersSubmitSubtitle')}
            nextLabel={t('selfSetup.tourDone')}
            onNext={() => onTourSubmitDone?.()}
            onDismiss={() => onTourDismiss?.()}
          >
            <Button
              type="submit"
              variant="contained"
              startIcon={isEdit ? <SaveIcon /> : <AddIcon />}
              sx={{ textTransform: 'none', px: 2.5 }}
            >
              {isEdit ? t('common.update') : t('common.create')}
            </Button>
          </SetupCoachmark>
        </DialogActions>
      </form>

      <Dialog
        open={mapPickerOpen}
        onClose={() => setMapPickerOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '85vh',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>{t('containers.form.mapPickerTitle')}</DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('containers.form.mapPickerHint')}
          </Typography>
          {mapPickerOpen && (
            <LocationMapPicker
              latitude={formData.latitude}
              longitude={formData.longitude}
              onLocationChange={handleLocationPick}
              height={420}
            />
          )}
          {formData.latitude != null && formData.longitude != null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {formData.latitude}, {formData.longitude}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setMapPickerOpen(false)} sx={{ textTransform: 'none' }}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
